// src/components/admin/ContractEditorDialog.js
import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import Modal, { ModalButton } from '../ui/Modal';
import { LoadingState } from '../ui/CrmPage';

const fmtCurrency = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 }).format(n) + ' ₸';
};

const recalcAmount = (price, quantity) => (Number(price) || 0) * (Number(quantity) || 0);

export default function ContractEditorDialog({ open, contractId, onClose, onSaved }) {
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);
  const isNew = !contractId;

  const [clients, setClients] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [products, setProducts] = useState([]);
  const [measures, setMeasures] = useState([]);

  const [contract, setContract] = useState({
    id: null, contract_number: '', contract_date: '',
    electronic_version: '', remaining_amount: 0,
    id_client: '', id_organization: '',
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addEmptyItem = () => {
    setItems((s) => [
      ...s,
      {
        tempId: (crypto?.randomUUID?.() || `tmp-${Date.now()}-${Math.random()}`),
        id_product: '', id_measure: '',
        product_price: '', product_quantity: '',
        product_amount: 0, product_balance: 0,
        _op: 'create',
      },
    ]);
  };

  const removeItem = (row) => {
    setItems((s) => {
      if (row.id) return s.map((it) => it === row ? { ...it, _op: 'delete' } : it);
      return s.filter((it) => it !== row);
    });
  };

  const patchItem = (row, patch) => {
    setItems((s) =>
      s.map((it) => {
        if (it !== row) return it;
        const next = { ...it, ...patch };
        const price = patch.product_price !== undefined ? patch.product_price : it.product_price;
        const quantity = patch.product_quantity !== undefined ? patch.product_quantity : it.product_quantity;
        next.product_amount = recalcAmount(price, quantity);
        next._op = it._op === 'create' ? 'create' : 'update';
        return next;
      })
    );
  };

  const loadDictionaries = async () => {
    const [rClients, rOrgs, rProd, rMeas] = await Promise.all([
      fetch(`${apiBase}v1/clients`).catch(() => null),
      fetch(`${apiBase}v1/organizations`).catch(() => null),
      fetch(`${apiBase}v1/products`).catch(() => null),
      fetch(`${apiBase}v1/measures`).catch(() => null),
    ]);
    if (rClients?.ok) setClients(await rClients.json());
    if (rOrgs?.ok)    setOrgs(await rOrgs.json());
    if (rProd?.ok)    setProducts(await rProd.json());
    if (rMeas?.ok)    setMeasures(await rMeas.json());
  };

  const loadContract = async () => {
    if (!contractId) {
      setContract({
        id: null, contract_number: '',
        contract_date: new Date().toISOString().slice(0, 10),
        electronic_version: '', remaining_amount: 0,
        id_client: '', id_organization: '',
      });
      setItems([]);
      return;
    }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${apiBase}v1/contracts/${contractId}`);
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Договор: HTTP ${r.status} ${t}`.trim());
      }
      const c = await r.json();
      setContract({
        id: c.id,
        contract_number: c.contract_number || '',
        contract_date: (c.contract_date || '').slice(0, 10),
        electronic_version: c.electronic_version || '',
        remaining_amount: Number(c.remaining_amount) || 0,
        id_client: c.id_client ?? '',
        id_organization: c.id_organization ?? '',
      });

      const rv = await fetch(`${apiBase}v1/contracts_view/${contractId}`);
      if (!rv.ok) {
        const t = await rv.text().catch(() => '');
        throw new Error(`Позиции: HTTP ${rv.status} ${t}`.trim());
      }
      const arr = await rv.json();
      const mapped = (Array.isArray(arr) ? arr : [])
        .filter((x) => x.id_contractitem)
        .map((x) => ({
          id: x.id_contractitem,
          id_product: String(x.id_product),
          id_measure: String(x.id_measure),
          product_price: x.product_price,
          product_quantity: x.product_quantity ?? 0,
          product_amount: x.product_amount ?? recalcAmount(x.product_price, x.product_quantity ?? 0),
          product_balance: x.product_balance ?? 0,
        }));
      setItems(mapped);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить договор');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      setError('');
      await loadDictionaries();
      await loadContract();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId, apiBase]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (!contract.contract_number) throw new Error('Укажите номер договора');
      if (!contract.contract_date) throw new Error('Укажите дату договора');
      if (!contract.id_client) throw new Error('Выберите клиента');
      if (!contract.id_organization) throw new Error('Выберите организацию');

      let savedContract;
      const body = {
        contract_number: contract.contract_number,
        contract_date: contract.contract_date,
        electronic_version: contract.electronic_version || null,
        remaining_amount: Number(contract.remaining_amount) || 0,
        id_client: Number(contract.id_client),
        id_organization: Number(contract.id_organization),
      };
      const url = isNew ? `${apiBase}v1/contracts` : `${apiBase}v1/contracts/${contract.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`${isNew ? 'Создание' : 'Обновление'}: HTTP ${res.status} ${t}`.trim());
      }
      savedContract = await res.json();
      const id_contract = savedContract.id;

      const ops = [];
      for (const it of items) {
        const id_product = Number(it.id_product);
        const id_measure = Number(it.id_measure);
        const price = Number(it.product_price) || 0;
        const quantity = Number(it.product_quantity) || 0;
        const amount = recalcAmount(price, quantity);

        if (it._op === 'delete' && it.id) {
          ops.push(fetch(`${apiBase}v1/contractitems/${it.id}`, { method: 'DELETE' }));
        } else if (it._op === 'create') {
          if (!id_product || !id_measure) continue;
          ops.push(fetch(`${apiBase}v1/contractitems`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_contracts: id_contract,
              id_product, id_measure,
              price, quantity, amount,
              // legacy aliases
              id_contract,
              product_price: price, product_quantity: quantity, product_amount: amount,
            }),
          }));
        } else if (it._op === 'update' && it.id) {
          ops.push(fetch(`${apiBase}v1/contractitems/${it.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_product, id_measure, price, quantity, amount,
              product_price: price, product_quantity: quantity, product_amount: amount,
            }),
          }));
        }
      }
      if (ops.length) {
        const results = await Promise.all(ops);
        const bad = results.find((r) => !r.ok);
        if (bad) {
          const t = await bad.text().catch(() => '');
          throw new Error(`Позиции: HTTP ${bad.status} ${t}`.trim());
        }
      }
      onSaved?.(id_contract);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const visibleItems = items.filter((it) => it._op !== 'delete');

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isNew ? 'Создать договор' : `Договор № ${contract.contract_number || '—'}`}
      subtitle={!isNew && contract.contract_date ? `Дата: ${contract.contract_date}` : null}
      size="xl"
      footer={
        <>
          <ModalButton variant="secondary" onClick={onClose}>Отмена</ModalButton>
          <ModalButton variant="primary" onClick={save} disabled={saving || loading}>
            <SaveIcon sx={{ fontSize: 16, marginRight: 4 }} />
            {saving ? 'Сохранение…' : 'Сохранить'}
          </ModalButton>
        </>
      }
    >
      {loading ? (
        <LoadingState text="Загружаем…" />
      ) : (
        <>
          {!!error && <div className="crmpage__alert crmpage__alert--error" style={{ marginBottom: 12 }}>{error}</div>}

          <section className="adminform__card" style={{ padding: 14, marginBottom: 14 }}>
            <div className="adminform__section-title">Реквизиты</div>
            <div className="adminform__grid">
              <label className="adminform__field">
                <span className="adminform__label">Номер<span className="req">*</span></span>
                <input
                  className="adminform__input"
                  value={contract.contract_number}
                  onChange={(e) => setContract((s) => ({ ...s, contract_number: e.target.value }))}
                />
              </label>
              <label className="adminform__field">
                <span className="adminform__label">Дата<span className="req">*</span></span>
                <input
                  type="date"
                  className="adminform__input"
                  value={contract.contract_date}
                  onChange={(e) => setContract((s) => ({ ...s, contract_date: e.target.value }))}
                />
              </label>
              <label className="adminform__field">
                <span className="adminform__label">Остаток, ₸</span>
                <input
                  className="adminform__input"
                  value={contract.remaining_amount}
                  onChange={(e) => setContract((s) => ({ ...s, remaining_amount: e.target.value }))}
                  inputMode="decimal"
                />
                <span className="adminform__hint">{fmtCurrency(contract.remaining_amount)}</span>
              </label>
              <label className="adminform__field">
                <span className="adminform__label">Электронная версия</span>
                <input
                  className="adminform__input"
                  value={contract.electronic_version}
                  onChange={(e) => setContract((s) => ({ ...s, electronic_version: e.target.value }))}
                  placeholder="URL или идентификатор"
                />
              </label>
              <label className="adminform__field">
                <span className="adminform__label">Клиент<span className="req">*</span></span>
                <select
                  className="adminform__select"
                  value={contract.id_client}
                  onChange={(e) => setContract((s) => ({ ...s, id_client: e.target.value }))}
                >
                  <option value="">— выберите —</option>
                  {clients.map((c) => <option key={c.id} value={String(c.id)}>{c.name || `#${c.id}`}</option>)}
                </select>
              </label>
              <label className="adminform__field">
                <span className="adminform__label">Организация<span className="req">*</span></span>
                <select
                  className="adminform__select"
                  value={contract.id_organization}
                  onChange={(e) => setContract((s) => ({ ...s, id_organization: e.target.value }))}
                >
                  <option value="">— выберите —</option>
                  {orgs.map((o) => <option key={o.id} value={String(o.id)}>{o.name || `#${o.id}`}</option>)}
                </select>
              </label>
            </div>
          </section>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="adminform__section-title" style={{ margin: 0 }}>Позиции договора</div>
            <button
              type="button"
              className="adminbtn"
              onClick={addEmptyItem}
              style={{ height: 32, padding: '0 12px', fontSize: 12.5 }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Добавить позицию
            </button>
          </div>

          <div className="crmpage__table-card" style={{ marginBottom: 4 }}>
            <div className="crmpage__table-wrap" style={{ maxHeight: 400 }}>
              <table className="crmpage__table">
                <colgroup>
                  <col />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 50 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Ед.</th>
                    <th style={{ textAlign: 'right' }}>Цена</th>
                    <th style={{ textAlign: 'right' }}>Кол-во</th>
                    <th style={{ textAlign: 'right' }}>Сумма</th>
                    <th style={{ textAlign: 'right' }}>Остаток</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((row) => {
                    const sum = recalcAmount(row.product_price, row.product_quantity);
                    return (
                      <tr key={row.id || row.tempId}>
                        <td>
                          <select
                            className="adminform__select"
                            value={row.id_product}
                            onChange={(e) => patchItem(row, { id_product: e.target.value })}
                          >
                            <option value="">— выберите —</option>
                            {products.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                          </select>
                        </td>
                        <td>
                          <select
                            className="adminform__select"
                            value={row.id_measure}
                            onChange={(e) => patchItem(row, { id_measure: e.target.value })}
                          >
                            <option value="">—</option>
                            {measures.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            className="adminform__input"
                            value={row.product_price}
                            onChange={(e) => patchItem(row, { product_price: e.target.value })}
                            inputMode="decimal"
                            style={{ textAlign: 'right' }}
                          />
                        </td>
                        <td>
                          <input
                            className="adminform__input"
                            value={row.product_quantity}
                            onChange={(e) => patchItem(row, { product_quantity: e.target.value })}
                            inputMode="decimal"
                            style={{ textAlign: 'right' }}
                          />
                        </td>
                        <td className="crmpage__num" style={{ textAlign: 'right' }}>{fmtCurrency(sum)}</td>
                        <td className="crmpage__num crmpage__muted" style={{ textAlign: 'right' }}>{row.product_balance ?? '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="adminbtn adminbtn--icon adminbtn--danger"
                            title="Удалить позицию"
                            onClick={() => removeItem(row)}
                          ><DeleteOutlineIcon sx={{ fontSize: 18 }} /></button>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleItems.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: '32px 12px' }}>
                        Позиции отсутствуют. Нажмите «Добавить позицию», чтобы внести строку.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
